import { useEffect } from 'react'

const modelCalls = [
  {
    window: '11:00 START',
    ecmwf: 'WSW 15 kt · G21',
    hrrr: 'WSW 12 kt · G20',
    confidence: 'HIGH',
    tone: 'text-emerald-300 border-emerald-300/25 bg-emerald-300/[0.06]',
    call: 'Fast reaching start. Win clear air and speed; do not gamble the race on a shoreline corner.',
  },
  {
    window: 'FRONT',
    ecmwf: 'Smooth W → NW · 16–17h',
    hrrr: 'Abrupt NNW · near 14h',
    confidence: 'MEDIUM',
    tone: 'text-amber-300 border-amber-300/25 bg-amber-300/[0.06]',
    call: 'Keep the lane reversible. HRRR is 2–3 hours earlier and more abrupt than ECMWF.',
  },
  {
    window: 'HARBOR BEACH',
    ecmwf: 'NNW/N 17–19 kt',
    hrrr: 'NNW/N 14–19 kt · gustier',
    confidence: 'HIGH DIR',
    tone: 'text-cyan-300 border-cyan-300/25 bg-cyan-300/[0.06]',
    call: 'The Michigan side should offer flatter water after the shift. Stay close enough to use it, not so close that the shore shadow owns us.',
  },
  {
    window: 'COVE SUNDAY AM',
    ecmwf: 'NNE 9 → 3 kt',
    hrrr: 'N 11 → 1 kt',
    confidence: 'HIGH FADE',
    tone: 'text-fuchsia-300 border-fuchsia-300/25 bg-fuchsia-300/[0.06]',
    call: 'The race compresses at the gate. Reaching Cove before the late-morning shutdown is the biggest strategic prize.',
  },
]

const phases = [
  {
    time: 'START → 14:00',
    title: 'Bank speed before the front',
    detail: 'Course north in WSW wind gives a fast reaching angle. Start in clear air, accelerate and keep the first lane simple. The front will turn this reach into a much tighter course.',
    action: 'Kite angle likely early. Be ready for a rapid header rather than overcommitting to maximum sail area.',
  },
  {
    time: '14:00 → EVENING',
    title: 'The front is the first decision gate',
    detail: 'HRRR turns NNW around 14:00; ECMWF veers more slowly through W and NW. A squall shift can arrive before the true front, so confirm it with sustained direction, pressure rise and cooler air.',
    action: 'Do not lock shore versus offshore until the new wind holds. After NW establishes, flatter Michigan-side water becomes valuable VMG.',
  },
  {
    time: 'SATURDAY NIGHT',
    title: 'Trade pressure against wave drag',
    detail: 'Both models settle into NNW/N. The official marine forecast is rougher than the model point forecasts, with roughly 5–9 ft possible across exposed water.',
    action: 'Choose the lane with the best speed through the sea, not automatically the lane with the highest wind number. Flat water can pay more than two extra knots.',
  },
  {
    time: 'SUNDAY MORNING',
    title: 'Race the ridge to Cove Island',
    detail: 'Both models collapse the northerly near Cove from roughly 8–11 kt to 1–4 kt late morning. The exact hour is uncertain by about two hours, but the shutdown signal is strong.',
    action: 'Minimize detours before the gate. If we arrive before the fade, turn west and bank distance. If caught in the collapse, sail to visible pressure rather than protecting the rhumbline.',
  },
  {
    time: 'AFTER COVE',
    title: 'The rebuild direction is less certain',
    detail: 'ECMWF returns wind from W/NW; HRRR backs farther toward WSW/SW. That difference changes the favored tack and where the first new pressure appears.',
    action: 'Leave the gate with lateral options. Follow the first sustained pressure line; do not pin the boat to one side based on a single model.',
  },
]

const gates = [
  {
    label: 'SHIFT CONFIRMED',
    rule: 'TWD holds above 300° for 20+ minutes with rising pressure.',
    call: 'Move from open-lake flexibility toward the flatter Michigan-side lane.',
  },
  {
    label: 'SQUALL, NOT FRONT',
    rule: 'Large short shift, pressure still falling or wind quickly returns WSW/W.',
    call: 'Treat it as local. Do not make the permanent lane decision yet.',
  },
  {
    label: 'COVE EARLY',
    rule: 'Arrival before roughly 09:00–10:00 Sunday with N/NNE still above 7 kt.',
    call: 'Cross promptly and bank the favorable westbound reaching miles.',
  },
  {
    label: 'COVE LATE',
    rule: 'Wind under 4 kt or fleet headings scatter around the gate.',
    call: 'Pressure beats distance. Protect motion and position for the first W-to-SW rebuild.',
  },
]

export function JoyrideRaceBrief() {
  useEffect(() => {
    document.title = 'Joyride Race Tactics | Bayview Mackinac 2026'
  }, [])

  return (
    <main className="min-h-screen bg-[#061018] text-white selection:bg-cyan-300 selection:text-slate-950">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, main { background: white !important; color: #07131d !important; }
          .print-card { break-inside: avoid; border-color: #cbd5e1 !important; background: white !important; }
          .print-muted { color: #334155 !important; }
        }
      `}</style>

      <div className="mx-auto max-w-4xl px-5 py-7 sm:px-8 sm:py-10">
        <header className="border-b border-cyan-300/25 pb-6">
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="font-mono text-xs font-semibold tracking-[0.24em] text-cyan-300">
              JOYRIDE · J/105 · COVE ISLAND
            </p>
            <button
              type="button"
              onClick={() => window.print()}
              className="no-print rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 transition hover:border-cyan-300/60 hover:text-cyan-200"
            >
              Print / save
            </button>
          </div>

          <h1 className="max-w-3xl text-4xl font-black tracking-[-0.04em] sm:text-6xl">
            Race tactics
          </h1>
          <p className="mt-3 text-base text-slate-300 sm:text-lg">
            Latest ECMWF + HRRR comparison · 11:00 EDT start
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="print-card rounded-xl border border-cyan-300/25 bg-cyan-300/10 p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-300">Start</p>
              <p className="mt-1 text-2xl font-black">WSW</p>
              <p className="text-xs text-slate-400">12–15 kt</p>
            </div>
            <div className="print-card rounded-xl border border-amber-300/20 bg-amber-300/[0.07] p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-amber-300">Front</p>
              <p className="mt-1 text-xl font-black">14:00–17:00</p>
              <p className="text-xs text-slate-400">W → NW/N</p>
            </div>
            <div className="print-card rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Night</p>
              <p className="mt-1 text-xl font-black">NNW / N</p>
              <p className="text-xs text-slate-400">14–20 kt</p>
            </div>
            <div className="print-card rounded-xl border border-fuchsia-300/20 bg-fuchsia-300/[0.07] p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-300">Cove</p>
              <p className="mt-1 text-xl font-black">Light hole</p>
              <p className="text-xs text-slate-400">Sunday late AM</p>
            </div>
          </div>
        </header>

        <section className="py-7">
          <div className="print-card rounded-2xl border border-cyan-300/30 bg-cyan-300/[0.08] p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">Primary race call</p>
            <p className="mt-3 text-xl font-bold leading-snug sm:text-2xl">
              Bank speed in the WSW reach, stay reversible through the front, use flatter water after the northwest arrives, and race to reach Cove before Sunday’s shutdown.
            </p>
          </div>
        </section>

        <section className="pb-8">
          <h2 className="text-sm font-black uppercase tracking-[0.18em] text-slate-400">ECMWF vs HRRR</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {modelCalls.map((item) => (
              <article key={item.window} className={`print-card rounded-2xl border p-5 ${item.tone}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono text-xs font-black tracking-wider">{item.window}</p>
                  <p className="rounded-full border border-current/40 px-2 py-1 text-[10px] font-black tracking-wider">{item.confidence}</p>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">ECMWF</p>
                    <p className="mt-1 font-bold text-slate-100">{item.ecmwf}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">HRRR</p>
                    <p className="mt-1 font-bold text-slate-100">{item.hrrr}</p>
                  </div>
                </div>
                <p className="print-muted mt-4 text-sm font-semibold leading-6 text-slate-300">{item.call}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="pb-8">
          <h2 className="text-sm font-black uppercase tracking-[0.18em] text-slate-400">Tactical sequence</h2>
          <div className="mt-4 space-y-3">
            {phases.map((phase) => (
              <article key={phase.time} className="print-card rounded-2xl border border-white/10 bg-white/[0.035] p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-5">
                  <div className="w-32 shrink-0 font-mono text-xs font-black tracking-wider text-cyan-300">{phase.time}</div>
                  <div>
                    <h3 className="text-lg font-black">{phase.title}</h3>
                    <p className="print-muted mt-1 text-sm leading-6 text-slate-300">{phase.detail}</p>
                    <p className="mt-3 border-l-2 border-cyan-300/60 pl-3 text-sm font-bold leading-6 text-cyan-100">{phase.action}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="pb-8">
          <h2 className="text-sm font-black uppercase tracking-[0.18em] text-slate-400">Decision gates</h2>
          <div className="mt-4 space-y-3">
            {gates.map((gate) => (
              <article key={gate.label} className="print-card rounded-2xl border border-amber-300/20 bg-amber-300/[0.055] p-5">
                <div className="grid gap-3 sm:grid-cols-[150px_1fr]">
                  <p className="font-mono text-xs font-black tracking-wider text-amber-300">{gate.label}</p>
                  <div>
                    <p className="print-muted text-sm leading-6 text-slate-300">{gate.rule}</p>
                    <p className="mt-1 text-sm font-black leading-6 text-slate-100">→ {gate.call}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="pb-8">
          <div className="print-card rounded-2xl border border-fuchsia-300/25 bg-fuchsia-300/[0.06] p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-fuchsia-300">What can decide the race</p>
            <p className="mt-3 text-lg font-black leading-snug">
              The models agree more on the start than on the finish. The largest gain is not guessing the start correctly; it is arriving at Cove before the northerly collapses and retaining options when the new west-to-southwest breeze returns.
            </p>
          </div>
        </section>

        <footer className="border-t border-white/10 pt-6">
          <p className="text-sm font-bold text-slate-300">Model basis</p>
          <p className="print-muted mt-2 text-xs leading-5 text-slate-500">
            Updated 10:47 EDT · Fresh 12Z ECMWF IFS and 12Z HRRR route-point comparison. Official marine forecasts carry a rougher 5–9 ft wave envelope and a squall watch; wave drag is treated here as a tactical lane and VMG variable. Exact front and ridge timing remain uncertain.
          </p>
        </footer>
      </div>
    </main>
  )
}

export default JoyrideRaceBrief
